"use client";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { Chip } from "@mui/material";
import React, { useMemo } from "react";

import MDAvatar from "@/components/atoms/MDAvatar";
import MDBadge from "@/components/atoms/MDBadge";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { useNotification } from "@/contexts/notification-context";
import { isSticker, unicodeToEmoji } from "@/helpers/messages-helpers";
import { getAvatarLetters } from "@/helpers/profile-helpers";
import { IChat } from "@/interfaces/chat.interfaces";
import { ChatContactListMessages } from "@/messages/chat/contact-list.messages";

type Props = {
  chat: IChat;
  messages: ChatContactListMessages;
  isSelected: boolean;
  setIsInitialLoad: (state: boolean) => void;
};

function ChatItem({ chat, isSelected, setIsInitialLoad, messages }: Props) {
  const { setSelectedChat } = useNotification();

  const { contactName, text, workflowName, userName, serviceQueueName } = chat;
  const unReadMessagesCount =
    chat.messages?.items?.filter((message) => !message.readDate).length ?? 0;
  const hasAttendant = !!userName && !workflowName;
  const hasAgent = !!workflowName;
  const queueLabel = serviceQueueName?.trim() || "";

  const containerStyle = () => {
    if (isSelected) return { backgroundColor: "#f0f2f5" };
    if (unReadMessagesCount > 0) return { backgroundColor: "#CAFFFF" };
    return {};
  };

  const handleChat = (selected: IChat) => {
    setIsInitialLoad(true);
    setSelectedChat(selected);
  };

  const displayText = (value: string | null) => {
    if (!value) return null;

    if (isSticker(value)) {
      return unicodeToEmoji(value);
    }

    return value;
  };

  const previewText = useMemo(() => {
    const value = displayText(text);
    if (!value) return null;

    return String(value).replace(/\s+/g, " ").trim();
  }, [text]);

  const assigneeName = hasAgent ? workflowName : userName;

  return (
    <MDBox
      p={1}
      borderRadius={"xl"}
      onClick={() => handleChat(chat)}
      sx={(theme) => ({
        "&:hover": { background: theme.palette.grey[300], cursor: "pointer" },
        ...containerStyle(),
        overflow: "hidden",
      })}
    >
      <MDBox display="flex" alignItems="flex-start" gap={2}>
        <MDAvatar size="md" bgColor="light">
          {getAvatarLetters(contactName)}
        </MDAvatar>

        <MDBox display="flex" flexDirection="column" flex={1} overflow="hidden" gap={0.25}>
          {(hasAttendant || hasAgent) && (
            <MDBox display="flex" alignItems="center" gap={0.5} overflow="hidden">
              {hasAgent ? (
                <SmartToyOutlinedIcon sx={{ fontSize: 14, color: "text.primary", flexShrink: 0 }} />
              ) : (
                <PersonOutlineIcon sx={{ fontSize: 14, color: "text.primary", flexShrink: 0 }} />
              )}
              <MDTypography
                variant="caption"
                fontWeight="medium"
                color="text"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {assigneeName}
              </MDTypography>
            </MDBox>
          )}

          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} overflow="hidden">
            <MDTypography
              variant="button"
              fontWeight="bold"
              color="dark"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {contactName}
            </MDTypography>

            {unReadMessagesCount > 0 && (
              <MDBadge size="xs" color="light" container circular badgeContent={1} />
            )}
          </MDBox>

          <MDTypography
            noWrap
            fontWeight="regular"
            color="text"
            title={previewText || undefined}
            sx={(theme) => ({
              display: "block",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: theme.typography.size.xs,
            })}
          >
            {previewText}
          </MDTypography>

          {!!queueLabel && (
            <MDBox mt={0.5}>
              <Chip
                size="small"
                variant="outlined"
                label={queueLabel}
                sx={(theme) => ({
                  height: 22,
                  borderRadius: theme.borders.borderRadius.md,
                  borderColor: theme.palette.grey[300],
                  bgcolor: theme.palette.grey[100],
                  "& .MuiChip-label": {
                    px: 1,
                    fontSize: theme.typography.size.xs,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.palette.text.primary,
                  },
                })}
              />
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export default ChatItem;
